import React, { useState, useEffect, Suspense, lazy } from "react";
import { CheckCircle, Menu, X } from "lucide-react";
import logo from "../images/racila_logo.jpg";

const UserButton = lazy(() => import('@clerk/clerk-react').then(m => ({ default: m.UserButton })));
const SignInButton = lazy(() => import('@clerk/clerk-react').then(m => ({ default: m.SignInButton })));
const SignedIn = lazy(() => import('@clerk/clerk-react').then(m => ({ default: m.SignedIn })));
const SignedOut = lazy(() => import('@clerk/clerk-react').then(m => ({ default: m.SignedOut })));

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("#page-top");
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false); // For blur effect

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "About", href: "/#about" },
    { label: "Services", href: "/#services" },
    { label: "Clients", href: "/#projects" },
    { label: "Contact", href: "/#contact" },
    { label: "Courses", href: "/courses" },
    { label: "Join Us", href: "/join-us" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-screen z-50 transition-all duration-300 ${
        isScrolled ? "bg-black text-white shadow-md" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo and Brand Name */}
        <a href="/" className="text-2xl font-bold relative">
          <img
            src={logo}
            alt="Racila Softech Logo"
            className={` w-16 h-auto border rounded-lg ${
              logoLoaded ? "blur-0" : "blur-sm"
            }`}
            width={319}
            height={230}
            fetchPriority="high"
            onLoad={() => setLogoLoaded(true)}
          />
        </a>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Desktop Navigation Menu */}
        <ul
          className={`hidden md:flex md:items-center md:space-x-6 md:relative w-full md:w-auto ${
            isScrolled ? "bg-black text-white" : "bg-transparent text-white"
          }`}
        >
          {navItems.map((item) => (
            <li key={item.href} className="relative">
              <a
                href={item.href}
                className={`block py-2 px-4 font-bold transition-all relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-0 after:h-1 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full ${
                  activeTab === item.href ? "after:w-full text-blue-600" : ""
                } ${isScrolled ? "text-white hover:text-blue-600" : "text-white hover:text-yellow-400"}`}
                onClick={() => setActiveTab(item.href)}
              >
                {item.label}
              </a>
            </li>
          ))}
          <li>
            <Suspense fallback={<div>Loading...</div>}>
              <SignedIn>
                <div className="text-white font-bold py-2 px-4 rounded cursor-pointer">
                  <UserButton afterSignOutUrl="/" />
                </div>
              </SignedIn>

              <SignedOut>
                <SignInButton mode="modal">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                    Sign In
                  </button>
                </SignInButton>
              </SignedOut>
            </Suspense>
          </li>
        </ul>

        {/* Mobile Navigation Menu */}
        {hasMounted && (
          <div
            className={`md:hidden fixed top-0 left-0 w-full h-full bg-black bg-opacity-90 transition-opacity duration-300 z-40 ${
              isOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"
            }`}
            onClick={() => setIsOpen(false)}
          >
            <ul
              className={`fixed top-0 left-0 w-full h-full flex flex-col items-center justify-center space-y-6 ${
                isOpen ? "translate-y-0" : "-translate-y-full"
              } transition-transform duration-300`}
            >
              {navItems.map((item) => (
                <li key={item.href} className="relative">
                  <a
                    href={item.href}
                    className={`block py-2 px-4 font-bold transition-all ${
                      activeTab === item.href ? "text-blue-600" : "text-white"
                    } hover:text-yellow-400`}
                    onClick={() => {
                      setActiveTab(item.href);
                      setIsOpen(false);
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li>
                <Suspense fallback={<div>Loading...</div>}>
                  <SignedIn>
                    <div className="text-white font-bold py-2 px-4 rounded cursor-pointer">
                      <UserButton afterSignOutUrl="/" />
                    </div>
                  </SignedIn>

                  <SignedOut>
                    <SignInButton mode="modal">
                      <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                        Sign In
                      </button>
                    </SignInButton>
                  </SignedOut>
                </Suspense>
              </li>
            </ul>
          </div>
        )}
      </div>
      
    </nav>
  );
};

export default Navigation;
